import React, { useEffect, useState } from 'react';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { PlusIcon, CalendarIcon, ClockIcon, UsersIcon, EditIcon, TrashIcon, MoreVerticalIcon, ImageIcon, AwardIcon, GlobeIcon, SettingsIcon, TrendingUpIcon, UserCheckIcon, ListIcon, CheckCircle2Icon, ActivityIcon, ChevronDownIcon, ChevronUpIcon, FileTextIcon, CircleIcon, SaveIcon, SendIcon, EyeIcon, ArrowLeftIcon } from 'lucide-react';
import { AgendaFeature, AgendaItem } from '../../../types/agenda';
import { AgendaSessionEdit } from './AgendaSessionEdit';
interface AgendaFeatureDetailProps {
  feature: AgendaFeature;
  onBack: () => void;
  onSave: (updatedFeature: Partial<AgendaFeature>) => void;
}
type TabType = 'configuration' | 'attendance' | 'agenda';
interface DayGroup {
  date: string;
  displayDate: string;
  intro: string;
  items: AgendaItem[];
}
// Mock sub-events data
const SUB_EVENTS = [{
  id: 'muscle',
  name: 'Dubai Muscle Show',
  shortName: 'DMS',
  color: '#8B5CF6'
}, {
  id: 'active',
  name: 'Dubai Active Show',
  shortName: 'DAS',
  color: '#3B82F6'
}, {
  id: 'industry',
  name: 'Dubai Active Industry',
  shortName: 'DAI',
  color: '#10B981'
}];
// Mock attendance data
const mockAttendanceData = {
  totalBooked: 145,
  totalInterested: 89,
  capacityUtilization: 78,
  recentBookings: [{
    id: '1',
    userName: 'Sarah Johnson',
    email: 'sarah@example.com',
    status: 'booked',
    time: '2 hours ago'
  }, {
    id: '2',
    userName: 'Mike Chen',
    email: 'mike@example.com',
    status: 'interested',
    time: '5 hours ago'
  }, {
    id: '3',
    userName: 'Emma Wilson',
    email: 'emma@example.com',
    status: 'booked',
    time: '1 day ago'
  }, {
    id: '4',
    userName: 'David Brown',
    email: 'david@example.com',
    status: 'booked',
    time: '1 day ago'
  }]
};
// Mock recent activity
const mockRecentActivity = [{
  id: '1',
  action: 'New session added: "Opening Keynote"',
  time: '2 hours ago'
}, {
  id: '2',
  action: 'Configuration updated: Booking enabled',
  time: '1 day ago'
}, {
  id: '3',
  action: '15 new bookings received',
  time: '2 days ago'
}, {
  id: '4',
  action: 'Feature published to app',
  time: '3 days ago'
}];
export function AgendaFeatureDetail({
  feature,
  onBack,
  onSave
}: AgendaFeatureDetailProps) {
  const [activeTab, setActiveTab] = useState<TabType>('configuration');
  const [activeActionMenu, setActiveActionMenu] = useState<string | null>(null);
  // Configuration state
  const [featureName, setFeatureName] = useState(feature.name);
  const [featureShortName, setFeatureShortName] = useState(feature.shortName);
  const [featureChair, setFeatureChair] = useState(feature.chairedBy || '');
  const [featureSubEvents, setFeatureSubEvents] = useState<string[]>(feature.subEventIds);
  const [bookingEnabled, setBookingEnabled] = useState(true);
  const [showInApp, setShowInApp] = useState(true);
  const [showOnWebsite, setShowOnWebsite] = useState(true);
  // Day intros state (keyed by date string)
  const [dayIntros, setDayIntros] = useState<Record<string, string>>({});
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const [editingDayIntro, setEditingDayIntro] = useState<string | null>(null);
  // Add Item modal state
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  const [itemTitle, setItemTitle] = useState('');
  const [itemStartDate, setItemStartDate] = useState('');
  const [itemStartTime, setItemStartTime] = useState('');
  const [itemEndTime, setItemEndTime] = useState('');
  // Edit session state
  const [editingSession, setEditingSession] = useState<AgendaItem | null>(null);
  // Toggle sub-event selection
  const toggleSubEvent = (subEventId: string) => {
    setFeatureSubEvents(prev => prev.includes(subEventId) ? prev.filter(id => id !== subEventId) : [...prev, subEventId]);
  };
  const handleSaveConfiguration = () => {
    onSave({
      name: featureName,
      shortName: featureShortName,
      chairedBy: featureChair || undefined,
      subEventIds: featureSubEvents
    });
    console.log('Configuration saved');
  };
  const handleAddItem = () => {
    const startDateTime = `${itemStartDate}T${itemStartTime}:00+04:00`;
    const endDateTime = itemEndTime ? `${itemStartDate}T${itemEndTime}:00+04:00` : `${itemStartDate}T${itemStartTime}:00+04:00`; // Use start time if end time not provided
    const newItem: AgendaItem = {
      id: `item-${Date.now()}`,
      startAt: startDateTime,
      endAt: endDateTime,
      title: itemTitle,
      description: {
        type: 'html',
        value: ''
      } // Empty description for now
    };
    // Update the feature with the new item
    onSave({
      items: [...feature.items, newItem]
    });
    setShowAddItemModal(false);
    setItemTitle('');
    setItemStartDate('');
    setItemStartTime('');
    setItemEndTime('');
  };
  const handleDeleteItem = (itemId: string) => {
    onSave({
      items: feature.items.filter(item => item.id !== itemId)
    });
  };
  const handleEditSession = (session: AgendaItem) => {
    setEditingSession(session);
  };
  const handleSaveSession = (updatedSession: AgendaItem) => {
    onSave({
      items: feature.items.map(item => item.id === updatedSession.id ? updatedSession : item)
    });
    setEditingSession(null);
  };
  const handleDeleteSession = (sessionId: string) => {
    onSave({
      items: feature.items.filter(item => item.id !== sessionId)
    });
    setEditingSession(null);
  };
  const handleEditItem = (item: AgendaItem) => {
    setEditingSession(item);
    // Parse the ISO datetime to get date and time
    const startDate = new Date(item.startAt);
    const endDate = new Date(item.endAt);
    setItemTitle(item.title);
    setItemStartDate(startDate.toISOString().split('T')[0]);
    setItemStartTime(startDate.toTimeString().slice(0, 5));
    // Only set end time if it's different from start time
    if (item.startAt !== item.endAt) {
      setItemEndTime(endDate.toTimeString().slice(0, 5));
    } else {
      setItemEndTime('');
    }
    setShowEditItemModal(true);
  };
  const handleSaveEditItem = () => {
    if (!editingSession) return;
    const startDateTime = `${itemStartDate}T${itemStartTime}:00+04:00`;
    const endDateTime = itemEndTime ? `${itemStartDate}T${itemEndTime}:00+04:00` : `${itemStartDate}T${itemStartTime}:00+04:00`;
    const updatedItem: AgendaItem = {
      ...editingSession,
      startAt: startDateTime,
      endAt: endDateTime,
      title: itemTitle
    };
    onSave({
      items: feature.items.map(item => item.id === editingSession.id ? updatedItem : item)
    });
    setShowEditItemModal(false);
    setEditingSession(null);
    setItemTitle('');
    setItemStartDate('');
    setItemStartTime('');
    setItemEndTime('');
  };
  const handleSaveDayIntro = (date: string, intro: string) => {
    setDayIntros(prev => ({
      ...prev,
      [date]: intro
    }));
    setEditingDayIntro(null);
  };
  const toggleDayExpanded = (date: string) => {
    setExpandedDays(prev => {
      const newSet = new Set(prev);
      if (newSet.has(date)) {
        newSet.delete(date);
      } else {
        newSet.add(date);
      }
      return newSet;
    });
  };
  const formatTime = (isoDateTime: string) => {
    const date = new Date(isoDateTime);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };
  const formatDate = (isoDateTime: string) => {
    const date = new Date(isoDateTime);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const formatDateKey = (isoDateTime: string) => {
    const date = new Date(isoDateTime);
    return date.toISOString().split('T')[0];
  };
  const formatDayHeader = (dateKey: string) => {
    const date = new Date(dateKey + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };
  // Group items by day
  const groupItemsByDay = (): DayGroup[] => {
    const groups: Record<string, DayGroup> = {};
    feature.items.forEach(item => {
      const dateKey = formatDateKey(item.startAt);
      if (!groups[dateKey]) {
        groups[dateKey] = {
          date: dateKey,
          displayDate: formatDayHeader(dateKey),
          intro: dayIntros[dateKey] || '',
          items: []
        };
      }
      groups[dateKey].items.push(item);
    });
    // Sort by date and sort items within each day by time
    return Object.values(groups).sort((a, b) => a.date.localeCompare(b.date)).map(group => ({
      ...group,
      items: group.items.sort((a, b) => a.startAt.localeCompare(b.startAt))
    }));
  };
  const getSubEventBadges = (subEventIds: string[]) => {
    if (subEventIds.length === 0) {
      return <Badge variant="default" className="bg-gray-100 text-gray-700" data-id="element-352">
          <GlobeIcon className="w-3 h-3 mr-1" data-id="element-353" />
          Global
        </Badge>;
    }
    return subEventIds.map(id => {
      const subEvent = SUB_EVENTS.find(se => se.id === id);
      if (!subEvent) return null;
      return <Badge key={id} className="text-white" style={{
        backgroundColor: subEvent.color
      }} data-id="element-354">
          {subEvent.shortName}
        </Badge>;
    });
  };
  const dayGroups = groupItemsByDay();
  // Expand first day by default on initial load
  useEffect(() => {
    if (dayGroups.length > 0 && expandedDays.size === 0) {
      setExpandedDays(new Set([dayGroups[0].date]));
    }
  }, [dayGroups.length, expandedDays.size]);
  // If editing a session, show the edit page
  if (editingSession) {
    return <AgendaSessionEdit session={editingSession} onBack={() => setEditingSession(null)} onSave={handleSaveSession} onDelete={handleDeleteSession} data-id="element-355" />;
  }
  return <div className="min-h-screen bg-gray-50 -m-8" data-id="element-356">
      {/* Header with Back Button and Feature Name */}
      <div className="bg-white border-b border-gray-200 px-8 py-6" data-id="element-357">
        <div className="flex items-center gap-4" data-id="element-358">
          <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-359">
            <ArrowLeftIcon className="w-5 h-5 text-gray-600" data-id="element-360" />
          </button>
          <div data-id="element-361">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-362">{feature.name}</h1>
            <p className="text-sm text-gray-500" data-id="element-363">
              {feature.shortName} • {feature.items.length} sessions
            </p>
          </div>
        </div>
      </div>

      {/* Tab Navigation - Sticky, Full Width */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-10" data-id="element-364">
        <div className="px-8" data-id="element-365">
          <div className="flex gap-8" data-id="element-366">
            <button onClick={() => setActiveTab('configuration')} className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'configuration' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-367">
              Configuration
            </button>
            <button onClick={() => setActiveTab('attendance')} className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'attendance' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-368">
              Attendance
            </button>
            <button onClick={() => setActiveTab('agenda')} className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'agenda' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-369">
              Manage Agenda
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-8 py-8" data-id="element-370">
        {/* Configuration Tab */}
        {activeTab === 'configuration' && <div className="max-w-5xl mx-auto space-y-6" data-id="element-371">
            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-372">
              <Card padding="lg" data-id="element-373">
                <div className="flex items-center justify-between mb-4" data-id="element-374">
                  <div data-id="element-375">
                    <h3 className="text-sm font-medium text-gray-500" data-id="element-376">
                      Total Sessions
                    </h3>
                    <div className="text-3xl font-bold text-gray-900 mt-1" data-id="element-377">
                      {feature.items.length}
                    </div>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-lg" data-id="element-378">
                    <CalendarIcon className="w-6 h-6 text-blue-600" data-id="element-379" />
                  </div>
                </div>
                <p className="text-xs text-gray-500" data-id="element-380">Scheduled sessions</p>
              </Card>

              <Card padding="lg" data-id="element-381">
                <div className="flex items-center justify-between mb-4" data-id="element-382">
                  <div data-id="element-383">
                    <h3 className="text-sm font-medium text-gray-500" data-id="element-384">
                      Total Bookings
                    </h3>
                    <div className="text-3xl font-bold text-gray-900 mt-1" data-id="element-385">
                      {mockAttendanceData.totalBooked}
                    </div>
                  </div>
                  <div className="p-3 bg-green-100 rounded-lg" data-id="element-386">
                    <UserCheckIcon className="w-6 h-6 text-green-600" data-id="element-387" />
                  </div>
                </div>
                <p className="text-xs text-gray-500" data-id="element-388">Confirmed attendees</p>
              </Card>

              <Card padding="lg" data-id="element-389">
                <div className="flex items-center justify-between mb-4" data-id="element-390">
                  <div data-id="element-391">
                    <h3 className="text-sm font-medium text-gray-500" data-id="element-392">
                      Interested
                    </h3>
                    <div className="text-3xl font-bold text-gray-900 mt-1" data-id="element-393">
                      {mockAttendanceData.totalInterested}
                    </div>
                  </div>
                  <div className="p-3 bg-purple-100 rounded-lg" data-id="element-394">
                    <UsersIcon className="w-6 h-6 text-purple-600" data-id="element-395" />
                  </div>
                </div>
                <p className="text-xs text-gray-500" data-id="element-396">Marked as interested</p>
              </Card>
            </div>

            {/* Recent Activity */}
            <Card padding="lg" data-id="element-397">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-398">
                Recent Activity
              </h3>
              <div className="space-y-3" data-id="element-399">
                {mockRecentActivity.map(activity => <div key={activity.id} className="flex items-start gap-3 pb-3 border-b border-gray-100 last:border-0 last:pb-0" data-id="element-400">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0" data-id="element-401">
                      <ActivityIcon className="w-4 h-4 text-blue-600" data-id="element-402" />
                    </div>
                    <div className="flex-1" data-id="element-403">
                      <p className="text-sm text-gray-900" data-id="element-404">{activity.action}</p>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-405">
                        {activity.time}
                      </p>
                    </div>
                  </div>)}
              </div>
            </Card>

            {/* Feature Settings */}
            <Card padding="lg" data-id="element-406">
              <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-407">
                Feature Settings
              </h3>
              <div className="space-y-6" data-id="element-408">
                <div className="grid grid-cols-2 gap-4" data-id="element-409">
                  <div data-id="element-410">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-411">
                      Feature Name *
                    </label>
                    <Input type="text" value={featureName} onChange={e => setFeatureName(e.target.value)} placeholder="e.g., XPRO Summit" data-id="element-412" />
                  </div>
                  <div data-id="element-413">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-414">
                      Short Name *
                    </label>
                    <Input type="text" value={featureShortName} onChange={e => setFeatureShortName(e.target.value)} placeholder="e.g., XPRO" data-id="element-415" />
                  </div>
                </div>

                <div data-id="element-416">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-417">
                    Chaired By
                  </label>
                  <Input type="text" value={featureChair} onChange={e => setFeatureChair(e.target.value)} placeholder="e.g., Industry Leaders Panel" data-id="element-418" />
                </div>

                <div data-id="element-419">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-420">
                    Sub-Event Assignment
                  </label>
                  <p className="text-xs text-gray-500 mb-3" data-id="element-421">
                    Select which sub-events this feature belongs to. Leave empty
                    for global (all events).
                  </p>
                  <div className="space-y-2 border border-gray-200 rounded-lg p-3" data-id="element-422">
                    {SUB_EVENTS.map(subEvent => <label key={subEvent.id} className="flex items-center gap-3 cursor-pointer p-2 rounded hover:bg-gray-50" data-id="element-423">
                        <input type="checkbox" checked={featureSubEvents.includes(subEvent.id)} onChange={() => toggleSubEvent(subEvent.id)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-424" />
                        <div className="w-3 h-3 rounded" style={{
                    backgroundColor: subEvent.color
                  }} data-id="element-425" />
                        <span className="text-sm text-gray-900" data-id="element-426">
                          {subEvent.name}
                        </span>
                        <span className="text-xs text-gray-500 font-mono" data-id="element-427">
                          ({subEvent.shortName})
                        </span>
                      </label>)}
                  </div>
                  {featureSubEvents.length === 0 && <p className="text-xs text-gray-500 mt-2 flex items-center gap-1" data-id="element-428">
                      <GlobeIcon className="w-3 h-3" data-id="element-429" />
                      This feature will be global (available in all sub-events)
                    </p>}
                </div>
              </div>
            </Card>

            {/* Booking & Visibility Settings */}
            <Card padding="lg" data-id="element-430">
              <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-431">
                Booking & Visibility
              </h3>
              <div className="space-y-6" data-id="element-432">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-id="element-433">
                  <div data-id="element-434">
                    <h4 className="text-sm font-medium text-gray-900" data-id="element-435">
                      Enable Booking
                    </h4>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-436">
                      Allow users to book sessions in this feature
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-437">
                    <input type="checkbox" checked={bookingEnabled} onChange={e => setBookingEnabled(e.target.checked)} className="sr-only peer" data-id="element-438" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" data-id="element-439"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-id="element-440">
                  <div data-id="element-441">
                    <h4 className="text-sm font-medium text-gray-900" data-id="element-442">
                      Show in App
                    </h4>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-443">
                      Display this feature in the mobile app
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-444">
                    <input type="checkbox" checked={showInApp} onChange={e => setShowInApp(e.target.checked)} className="sr-only peer" data-id="element-445" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" data-id="element-446"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-id="element-447">
                  <div data-id="element-448">
                    <h4 className="text-sm font-medium text-gray-900" data-id="element-449">
                      Show on Website
                    </h4>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-450">
                      Display this feature on the event website
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-451">
                    <input type="checkbox" checked={showOnWebsite} onChange={e => setShowOnWebsite(e.target.checked)} className="sr-only peer" data-id="element-452" />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" data-id="element-453"></div>
                  </label>
                </div>
              </div>

              <div className="mt-6" data-id="element-454">
                <Button variant="primary" onClick={handleSaveConfiguration} data-id="element-455">
                  Save Configuration
                </Button>
              </div>
            </Card>
          </div>}

        {/* Attendance Tab */}
        {activeTab === 'attendance' && <div className="max-w-5xl mx-auto space-y-6" data-id="element-456">
            {/* Attendance Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-457">
              <Card padding="lg" data-id="element-458">
                <div className="flex items-center justify-between" data-id="element-459">
                  <div data-id="element-460">
                    <h3 className="text-sm font-medium text-gray-500" data-id="element-461">
                      Booked
                    </h3>
                    <div className="text-3xl font-bold text-gray-900 mt-1" data-id="element-462">
                      {mockAttendanceData.totalBooked}
                    </div>
                  </div>
                  <div className="p-3 bg-green-100 rounded-lg" data-id="element-463">
                    <CheckCircle2Icon className="w-6 h-6 text-green-600" data-id="element-464" />
                  </div>
                </div>
              </Card>

              <Card padding="lg" data-id="element-465">
                <div className="flex items-center justify-between" data-id="element-466">
                  <div data-id="element-467">
                    <h3 className="text-sm font-medium text-gray-500" data-id="element-468">
                      Interested
                    </h3>
                    <div className="text-3xl font-bold text-gray-900 mt-1" data-id="element-469">
                      {mockAttendanceData.totalInterested}
                    </div>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-lg" data-id="element-470">
                    <UsersIcon className="w-6 h-6 text-blue-600" data-id="element-471" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Recent Bookings */}
            <Card padding="lg" data-id="element-472">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-473">
                Recent Bookings
              </h3>
              <div className="space-y-3" data-id="element-474">
                {mockAttendanceData.recentBookings.map(booking => <div key={booking.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg" data-id="element-475">
                    <div className="flex items-center gap-3" data-id="element-476">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-477">
                        <span className="text-sm font-bold text-blue-600" data-id="element-478">
                          {booking.userName.charAt(0)}
                        </span>
                      </div>
                      <div data-id="element-479">
                        <div className="text-sm font-medium text-gray-900" data-id="element-480">
                          {booking.userName}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-481">
                          {booking.email}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4" data-id="element-482">
                      <Badge variant={booking.status === 'booked' ? 'success' : 'default'} data-id="element-483">
                        {booking.status}
                      </Badge>
                      <span className="text-xs text-gray-500" data-id="element-484">
                        {booking.time}
                      </span>
                    </div>
                  </div>)}
              </div>
            </Card>
          </div>}

        {/* Manage Agenda Tab - Grouped by Day */}
        {activeTab === 'agenda' && <div className="max-w-7xl mx-auto flex gap-8" data-id="element-485">
            {/* Main Content - Centered */}
            <div className="flex-1 max-w-4xl mx-auto space-y-6" data-id="element-486">
              <div className="flex justify-between items-center" data-id="element-487">
                <div data-id="element-488">
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-489">
                    Manage Sessions
                  </h2>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-490">
                    Sessions organized by day with optional introductions
                  </p>
                </div>
                <Button variant="primary" onClick={() => setShowAddItemModal(true)} data-id="element-491">
                  <PlusIcon className="w-4 h-4 mr-2" data-id="element-492" />
                  Add Session
                </Button>
              </div>

              {/* Day Groups */}
              {dayGroups.length === 0 ? <div className="bg-white rounded-lg border border-gray-200 p-12 text-center" data-id="element-493">
                  <CalendarIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" data-id="element-494" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-495">
                    No sessions yet
                  </h3>
                  <p className="text-gray-500 mb-4" data-id="element-496">
                    Add your first session to this feature
                  </p>
                  <Button variant="primary" onClick={() => setShowAddItemModal(true)} data-id="element-497">
                    <PlusIcon className="w-4 h-4 mr-2" data-id="element-498" />
                    Add Session
                  </Button>
                </div> : <div className="space-y-6" data-id="element-499">
                  {dayGroups.map(dayGroup => <Card key={dayGroup.date} padding="none" className="overflow-hidden" data-id="element-500">
                      {/* Day Header */}
                      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-200" data-id="element-501">
                        <div className="p-6" data-id="element-502">
                          <div className="flex items-center justify-between mb-3" data-id="element-503">
                            <div className="flex items-center gap-3" data-id="element-504">
                              <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm" data-id="element-505">
                                <CalendarIcon className="w-6 h-6 text-blue-600" data-id="element-506" />
                              </div>
                              <div data-id="element-507">
                                <h3 className="text-lg font-bold text-gray-900" data-id="element-508">
                                  {dayGroup.displayDate}
                                </h3>
                                <p className="text-sm text-gray-600" data-id="element-509">
                                  {dayGroup.items.length} session
                                  {dayGroup.items.length !== 1 ? 's' : ''}
                                </p>
                              </div>
                            </div>
                            <button onClick={() => toggleDayExpanded(dayGroup.date)} className="p-2 hover:bg-white/50 rounded-lg transition-colors" data-id="element-510">
                              {expandedDays.has(dayGroup.date) ? <ChevronUpIcon className="w-5 h-5 text-gray-600" data-id="element-511" /> : <ChevronDownIcon className="w-5 h-5 text-gray-600" data-id="element-512" />}
                            </button>
                          </div>

                          {/* Day Intro */}
                          {editingDayIntro === dayGroup.date ? <div className="space-y-2" data-id="element-513">
                              <textarea value={dayIntros[dayGroup.date] || ''} onChange={e => setDayIntros(prev => ({
                      ...prev,
                      [dayGroup.date]: e.target.value
                    }))} placeholder="Add an optional introduction for this day..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm min-h-[80px] bg-white" data-id="element-514" />
                              <div className="flex gap-2" data-id="element-515">
                                <Button variant="primary" size="sm" onClick={() => handleSaveDayIntro(dayGroup.date, dayIntros[dayGroup.date] || '')} data-id="element-516">
                                  Save Intro
                                </Button>
                                <Button variant="outline" size="sm" onClick={() => setEditingDayIntro(null)} data-id="element-517">
                                  Cancel
                                </Button>
                              </div>
                            </div> : <div data-id="element-518">
                              {dayGroup.intro || dayIntros[dayGroup.date] ? <div className="bg-white/70 rounded-lg p-3 border border-gray-200" data-id="element-519">
                                  <div className="flex items-start justify-between gap-3" data-id="element-520">
                                    <div className="flex items-start gap-2 flex-1" data-id="element-521">
                                      <FileTextIcon className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" data-id="element-522" />
                                      <p className="text-sm text-gray-700 leading-relaxed" data-id="element-523">
                                        {dayIntros[dayGroup.date] || dayGroup.intro}
                                      </p>
                                    </div>
                                    <button onClick={() => setEditingDayIntro(dayGroup.date)} className="text-blue-600 hover:text-blue-700 text-xs font-medium flex-shrink-0" data-id="element-524">
                                      Edit
                                    </button>
                                  </div>
                                </div> : <button onClick={() => setEditingDayIntro(dayGroup.date)} className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1" data-id="element-525">
                                  <PlusIcon className="w-4 h-4" data-id="element-526" />
                                  Add day introduction
                                </button>}
                            </div>}
                        </div>
                      </div>

                      {/* Sessions List */}
                      {expandedDays.has(dayGroup.date) && <div className="p-6 space-y-4" data-id="element-527">
                          {dayGroup.items.map(item => <div key={item.id} onClick={() => handleEditSession(item)} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all cursor-pointer group" data-id="element-528">
                              {/* Time */}
                              <div className="flex-shrink-0 text-center min-w-[80px]" data-id="element-529">
                                <div className="text-sm font-semibold text-gray-900" data-id="element-530">
                                  {formatTime(item.startAt)}
                                </div>
                                {item.startAt !== item.endAt && <>
                                    <div className="text-xs text-gray-500" data-id="element-531">
                                      to
                                    </div>
                                    <div className="text-sm font-semibold text-gray-900" data-id="element-532">
                                      {formatTime(item.endAt)}
                                    </div>
                                  </>}
                              </div>

                              {/* Content */}
                              <div className="flex-1" data-id="element-533">
                                <h4 className="text-base font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors" data-id="element-534">
                                  {item.title}
                                </h4>

                                {/* Badges */}
                                <div className="flex items-center gap-2 mb-2" data-id="element-535">
                                  {item.accreditedLogo && <span className="inline-flex items-center px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded" data-id="element-536">
                                      <AwardIcon className="w-3 h-3 mr-1" data-id="element-537" />
                                      Accredited
                                    </span>}
                                  {item.image && <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded" data-id="element-538">
                                      <ImageIcon className="w-3 h-3 mr-1" data-id="element-539" />
                                      Has Image
                                    </span>}
                                  {item.speakerGroups && item.speakerGroups.length > 0 && <span className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded" data-id="element-540">
                                        <UsersIcon className="w-3 h-3 mr-1" data-id="element-541" />
                                        {item.speakerGroups.reduce((acc, g) => acc + g.speakers.length, 0)}{' '}
                                        speaker(s)
                                      </span>}
                                </div>

                                {/* Speaker Groups */}
                                {item.speakerGroups && item.speakerGroups.length > 0 && <div className="space-y-1 mt-2" data-id="element-542">
                                      {item.speakerGroups.map(group => <div key={group.id} className="text-sm" data-id="element-543">
                                          <span className="font-medium text-gray-700" data-id="element-544">
                                            {group.title}:
                                          </span>
                                          <span className="text-gray-600 ml-2" data-id="element-545">
                                            {group.speakers.length} speaker(s)
                                          </span>
                                        </div>)}
                                    </div>}
                              </div>

                              {/* Delete Button */}
                              <div className="relative" data-id="element-546">
                                <button onClick={e => {
                      e.stopPropagation();
                      if (window.confirm('Are you sure you want to delete this session?')) {
                        handleDeleteItem(item.id!);
                      }
                    }} className="text-gray-400 hover:text-red-600 p-2 rounded-full hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100" title="Delete session" data-id="element-547">
                                  <TrashIcon className="w-5 h-5" data-id="element-548" />
                                </button>
                              </div>
                            </div>)}
                        </div>}
                    </Card>)}
                </div>}
            </div>

            {/* Sticky Sidebar - Agenda Completion Widget */}
            <div className="w-80 flex-shrink-0" data-id="element-549">
              <div className="sticky top-32" data-id="element-550">
                <Card padding="lg" data-id="element-551">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4" data-id="element-552">
                    Agenda Publishing
                  </h3>

                  {/* Published Status */}
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-553">
                    <div className="flex items-center gap-2 mb-2" data-id="element-554">
                      <CircleIcon className="w-4 h-4 text-gray-400" data-id="element-555" />
                      <span className="text-sm font-semibold text-gray-900" data-id="element-556">
                        Not Published
                      </span>
                    </div>
                    <p className="text-xs text-gray-500" data-id="element-557">
                      This agenda has not been published yet
                    </p>
                  </div>

                  {/* Quick Stats */}
                  <div className="mb-6 space-y-2 pb-6 border-b border-gray-200" data-id="element-558">
                    <div className="flex items-center justify-between text-sm" data-id="element-559">
                      <span className="text-gray-600" data-id="element-560">Total Sessions</span>
                      <span className="font-semibold text-gray-900" data-id="element-561">
                        {feature.items.length}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm" data-id="element-562">
                      <span className="text-gray-600" data-id="element-563">Days</span>
                      <span className="font-semibold text-gray-900" data-id="element-564">
                        {dayGroups.length}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm" data-id="element-565">
                      <span className="text-gray-600" data-id="element-566">With Speakers</span>
                      <span className="font-semibold text-gray-900" data-id="element-567">
                        {feature.items.filter(item => item.speakerGroups && item.speakerGroups.length > 0).length}
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3" data-id="element-568">
                    <Button variant="outline" className="w-full" data-id="element-569">
                      <EyeIcon className="w-4 h-4 mr-2" data-id="element-570" />
                      Preview Agenda
                    </Button>

                    <Button variant="primary" disabled={feature.items.length === 0} className="w-full" data-id="element-571">
                      <SendIcon className="w-4 h-4 mr-2" data-id="element-572" />
                      Publish to App & Website
                    </Button>
                  </div>

                  {feature.items.length === 0 && <p className="text-xs text-gray-500 text-center mt-3" data-id="element-573">
                      Add sessions to enable publishing
                    </p>}
                </Card>
              </div>
            </div>
          </div>}
      </div>

      {/* Add Item Modal */}
      {showAddItemModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-574">
          <Card className="w-full max-w-lg" data-id="element-575">
            <div className="p-6" data-id="element-576">
              <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-577">
                Add Session
              </h3>
              <div className="space-y-4" data-id="element-578">
                <div data-id="element-579">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-580">
                    Session Title *
                  </label>
                  <textarea placeholder="e.g., Opening Keynote: The Future of Fitness Technology" value={itemTitle} onChange={e => setItemTitle(e.target.value)} className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm min-h-[80px] resize-none" data-id="element-581" />
                </div>
                <div data-id="element-582">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-583">
                    Date *
                  </label>
                  <Input type="date" value={itemStartDate} onChange={e => setItemStartDate(e.target.value)} data-id="element-584" />
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-585">
                  <div data-id="element-586">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-587">
                      Start Time *
                    </label>
                    <Input type="time" value={itemStartTime} onChange={e => setItemStartTime(e.target.value)} data-id="element-588" />
                  </div>
                  <div data-id="element-589">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-590">
                      End Time
                    </label>
                    <Input type="time" value={itemEndTime} onChange={e => setItemEndTime(e.target.value)} placeholder="Optional" data-id="element-591" />
                    <p className="text-xs text-gray-500 mt-1" data-id="element-592">Optional</p>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 mt-6" data-id="element-593">
                <Button variant="outline" onClick={() => setShowAddItemModal(false)} className="flex-1" data-id="element-594">
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleAddItem} disabled={!itemTitle || !itemStartDate || !itemStartTime} className="flex-1" data-id="element-595">
                  Add Session
                </Button>
              </div>
            </div>
          </Card>
        </div>}
    </div>;
}